import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useKitchen } from "../contexts/KitchenContext";
import { storesAPI, adminAPI, usersAPI, menuAPI, formatError } from "../utils/api";
import { useConfirm } from "../hooks/useConfirm";

import {
  ArrowDown, CalendarDays, TrendingUp, Check, Plus, X,
  Trash2, Copy, Loader, ChevronRight, AlertTriangle, Sparkles, Lock
} from "lucide-react";

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
}

function calcExpiry(dateStr, shelfDays) {
  if (!shelfDays) return "";
  return addDays(dateStr, shelfDays);
}

const MEAL_TYPES = ["breakfast", "lunch", "snack"];
const MEAL_COLORS = {
  breakfast: "border-amber-200 bg-amber-50 text-amber-700",
  lunch: "border-green-200 bg-green-50 text-green-700",
  snack: "border-purple-200 bg-purple-50 text-purple-700"
};

// ─── SECTION WRAPPER ─────────────────────────────────────────────────────────
function Section({ title, icon: Icon, color, children, badge }) {
  return (
    <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden shadow-sm">
      <div className={`px-5 py-4 border-b border-[#E5E1DB] flex items-center justify-between ${color || "bg-[#FAFAF8]"}`}>
        <div className="flex items-center gap-3">
          <Icon size={18} className="text-[#E8956D]" />
          <h2 className="font-bold text-[#3A322C] font-[Manrope]">{title}</h2>
          {badge}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ─── MULTI-ROW STOCK INWARD ───────────────────────────────────────────────────
function StockInwardSection({ kitchens, masterItems }) {
  const today = new Date().toISOString().split("T")[0];
  const { confirm, ConfirmModal } = useConfirm();
  const [date, setDate] = useState(today);
  const [kitchenId, setKitchenId] = useState(kitchens[0]?.id || "");
  const [supplier, setSupplier] = useState("");
  const [billNo, setBillNo] = useState("");
  const [rows, setRows] = useState([newRow()]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => { if (kitchens.length > 0 && !kitchenId) setKitchenId(kitchens[0].id); }, [kitchens]);

  function newRow() { return { item_id: "", qty: "", unit_price: "", batch_number: "", expiry_date: "", notes: "" }; }

  const addRows = (n = 1) => setRows(r => [...r, ...Array(n).fill(null).map(newRow)]);

  const updateRow = (i, field, val) => {
    setRows(prev => {
      const updated = [...prev];
      updated[i] = { ...updated[i], [field]: val };
      // Auto-fill expiry for perishable items
      if (field === "item_id") {
        const item = masterItems.find(m => m.id === val);
        if (item?.is_perishable && item.default_shelf_life_days > 0) {
          updated[i].expiry_date = calcExpiry(date, item.default_shelf_life_days);
        } else {
          updated[i].expiry_date = "";
        }
      }
      if (field === "date" && rows[i].item_id) {
        const item = masterItems.find(m => m.id === rows[i].item_id);
        if (item?.is_perishable && item.default_shelf_life_days > 0) {
          updated[i].expiry_date = calcExpiry(val, item.default_shelf_life_days);
        }
      }
      return updated;
    });
  };

  const removeRow = (i) => setRows(r => r.length > 1 ? r.filter((_, j) => j !== i) : r);

  const getItem = (id) => masterItems.find(m => m.id === id);

  const filteredItems = masterItems.filter(i =>
    !search || i.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSubmit = async () => {
    const valid = rows.filter(r => r.item_id && parseFloat(r.qty) > 0);
    if (valid.length === 0) { setError("Add at least one item with quantity"); return; }
    for (const r of valid) {
      const item = getItem(r.item_id);
      if (item?.is_perishable && !r.expiry_date) {
        setError(`Expiry date required for perishable item: ${item.name}`);
        return;
      }
    }
    setSaving(true); setError("");
    let ok = 0, fail = 0;
    for (const r of valid) {
      try {
        await storesAPI.createInward({
          item_id: r.item_id,
          quantity: parseFloat(r.qty),
          kitchen_id: kitchenId,
          supplier,
          bill_number: billNo,
          date,
          unit_price: r.unit_price ? parseFloat(r.unit_price) : null,
          batch_number: r.batch_number || "",
          expiry_date: r.expiry_date || null,
          notes: r.notes || "",
        });
        ok++;
      } catch { fail++; }
    }
    setSaving(false);
    if (ok > 0) {
      setSuccess(`${ok} item(s) recorded successfully!${fail > 0 ? ` (${fail} failed)` : ""}`);
      setRows([newRow()]);
      setBillNo("");
      setTimeout(() => setSuccess(""), 4000);
    } else { setError("All entries failed. Please check and retry."); }
  };

  return (
    <div className="space-y-4">
      {/* Header controls */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
          <input type="date" value={date} onChange={e => { setDate(e.target.value); }}
            className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen</label>
          <select value={kitchenId} onChange={e => setKitchenId(e.target.value)}
            className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
            {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Supplier (common)</label>
          <input value={supplier} onChange={e => setSupplier(e.target.value)}
            placeholder="Supplier name" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Bill Number</label>
          <input value={billNo} onChange={e => setBillNo(e.target.value)}
            placeholder="e.g. BILL-001" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
        </div>
      </div>

      {/* Item search filter */}
      <div>
        <input placeholder="Filter items by name..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] mb-3" />
      </div>

      {/* Rows table */}
      <div className="border border-[#E5E1DB] rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_80px_90px_90px_110px_60px_32px] bg-[#F4F3F0] px-3 py-2.5 gap-2">
          <span className="text-xs font-bold uppercase text-[#5C5147]">Item</span>
          <span className="text-xs font-bold uppercase text-[#5C5147]">Qty</span>
          <span className="text-xs font-bold uppercase text-[#E8956D]">Rate (₹)</span>
          <span className="text-xs font-bold uppercase text-[#5C5147]">Batch No.</span>
          <span className="text-xs font-bold uppercase text-[#D98032]">Expiry Date</span>
          <span className="text-xs font-bold uppercase text-[#5C5147]">Notes</span>
          <span />
        </div>
        <div className="divide-y divide-[#F4F3F0] max-h-80 overflow-y-auto">
          {rows.map((row, i) => {
            const item = getItem(row.item_id);
            const isPerish = item?.is_perishable;
            return (
              <div key={`idx-${i}`} className={`grid grid-cols-[1fr_80px_90px_90px_110px_60px_32px] px-3 py-2.5 gap-2 items-center ${isPerish && !row.expiry_date ? "bg-[#FDF4EB]/30" : ""}`}>
                <select value={row.item_id} onChange={e => updateRow(i, "item_id", e.target.value)}
                  className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D] w-full">
                  <option value="">Select item</option>
                  {filteredItems.map(m => (
                    <option key={m.id} value={m.id}>{m.item_id}. {m.name}{m.is_perishable ? " 🕐" : ""} ({m.unit})</option>
                  ))}
                </select>
                <input type="number" step="0.01" min="0" placeholder="0"
                  value={row.qty} onChange={e => updateRow(i, "qty", e.target.value)}
                  className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs text-center outline-none focus:border-[#E8956D] w-full" />
                <div className="relative">
                  <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[#8A7E73] text-[10px] font-bold">₹</span>
                  <input type="number" step="0.01" min="0" placeholder="0"
                    value={row.unit_price} onChange={e => updateRow(i, "unit_price", e.target.value)}
                    title="Purchase price per unit — powers Recipe Cost Calculator"
                    className="border border-[#E8956D]/50 rounded-lg pl-4 pr-1 py-1.5 text-xs outline-none focus:border-[#E8956D] w-full bg-[#FDF4EB]/20" />
                </div>
                <input placeholder="LOT-001"
                  value={row.batch_number} onChange={e => updateRow(i, "batch_number", e.target.value)}
                  className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D] w-full" />
                <input type="date"
                  value={row.expiry_date}
                  onChange={e => updateRow(i, "expiry_date", e.target.value)}
                  className={`border rounded-lg px-2 py-1.5 text-xs outline-none w-full transition-colors ${isPerish && !row.expiry_date ? "border-[#D98032] bg-[#FDF4EB]/50 focus:border-[#D98032]" : "border-[#E5E1DB] focus:border-[#E8956D]"}`} />
                <input placeholder="Notes"
                  value={row.notes} onChange={e => updateRow(i, "notes", e.target.value)}
                  className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D] w-full" />
                <button onClick={() => removeRow(i)} className="text-[#C4BAAF] hover:text-[#C2624E]">
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add rows */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => addRows(1)} className="text-xs border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-[#5C5147] hover:bg-[#F4F3F0] flex items-center gap-1"><Plus size={12} /> Add Row</button>
        <button onClick={() => addRows(5)} className="text-xs border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-[#5C5147] hover:bg-[#F4F3F0] flex items-center gap-1"><Plus size={12} /> Add 5 Rows</button>
        <button onClick={() => setRows([newRow()])} className="text-xs text-[#C2624E] hover:underline ml-1">Clear All</button>
        <span className="ml-auto text-xs text-[#8A7E73] self-center">{rows.filter(r => r.item_id && r.qty).length} item(s) ready to save</span>
      </div>

      {error && <div className="bg-[#FBEAE7] rounded-lg px-4 py-2.5 text-sm text-[#C2624E]">{error}</div>}
      {success && <div className="bg-[#EDF2EE] rounded-lg px-4 py-2.5 text-sm text-[#637C64] flex items-center gap-2"><Check size={15} />{success}</div>}

      <button onClick={handleSubmit} disabled={saving}
        className="w-full bg-[#E8956D] text-[#3A322C] rounded-xl py-3 font-bold flex items-center justify-center gap-2 hover:bg-[#D48661] disabled:opacity-60 transition-colors">
        {saving ? <Loader size={16} className="animate-spin" /> : <ArrowDown size={16} />}
        {saving ? "Saving..." : `Record ${rows.filter(r => r.item_id && r.qty).length || ""} Stock Inward Entries`}
      </button>
    </div>
  );
}

// ─── DAILY MENU PLAN ──────────────────────────────────────────────────────────
function MenuPlanSection({ kitchens }) {
  const today = new Date().toISOString().split("T")[0];
  const yesterday = addDays(today, -1);
  const [date, setDate] = useState(today);
  const [kitchenId, setKitchenId] = useState(kitchens[0]?.id || "");
  const [menuItems, setMenuItems] = useState([]);
  const [plan, setPlan] = useState({ breakfast: [], lunch: [], snack: [], notes: "" });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loadingPlan, setLoadingPlan] = useState(false);

  useEffect(() => { if (kitchens.length > 0 && !kitchenId) setKitchenId(kitchens[0].id); }, [kitchens]);
  useEffect(() => { loadMenuItems(); }, []);
  useEffect(() => { if (kitchenId && date) loadPlan(date); }, [date, kitchenId]);

  const loadMenuItems = async () => {
    try { const r = await menuAPI.getItems({}); setMenuItems(r.data || []); }
    catch (e) { if (process.env.NODE_ENV === 'development') console.error('menuItems fetch:', e); }
  };

  const loadPlan = async (d) => {
    if (!kitchenId) return;
    setLoadingPlan(true);
    try {
      const r = await menuAPI.getPlanByDate(d, kitchenId);
      if (r.data) {
        setPlan({ breakfast: r.data.breakfast || [], lunch: r.data.lunch || [], snack: r.data.snack || [], notes: r.data.notes || "" });
      } else {
        setPlan({ breakfast: [], lunch: [], snack: [], notes: "" });
      }
    } catch { setPlan({ breakfast: [], lunch: [], snack: [], notes: "" }); }
    setLoadingPlan(false);
  };

  const copyFromPrevious = async () => {
    try {
      const r = await menuAPI.getPlanByDate(yesterday, kitchenId);
      if (r.data) {
        setPlan({ breakfast: r.data.breakfast || [], lunch: r.data.lunch || [], snack: r.data.snack || [], notes: "" });
        setSuccess("Copied from yesterday's plan!");
        setTimeout(() => setSuccess(""), 2000);
      } else {
        setError("No plan found for yesterday");
        setTimeout(() => setError(""), 2000);
      }
    } catch { setError("Could not load yesterday's plan"); }
  };

  const addMealItem = (mealType, menuItemId) => {
    if (!menuItemId) return;
    const exists = plan[mealType].some(x => x.menu_item_id === menuItemId);
    if (exists) return;
    const mi = menuItems.find(i => i.id === menuItemId);
    setPlan(p => ({ ...p, [mealType]: [...p[mealType], { menu_item_id: menuItemId, servings: 0, menu_item_name: mi?.name || "" }] }));
  };

  const updateServings = (mealType, idx, val) => {
    setPlan(p => {
      const meal = [...p[mealType]];
      meal[idx] = { ...meal[idx], servings: parseInt(val) || 0 };
      return { ...p, [mealType]: meal };
    });
  };

  const removeItem = (mealType, idx) => setPlan(p => ({ ...p, [mealType]: p[mealType].filter((_, i) => i !== idx) }));

  const totalServings = [...plan.breakfast, ...plan.lunch, ...plan.snack].reduce((s, i) => s + (i.servings || 0), 0);

  const savePlan = async () => {
    setSaving(true); setError("");
    try {
      await menuAPI.savePlan({
        date,
        kitchen_id: kitchenId,
        breakfast: plan.breakfast.map(x => ({ menu_item_id: x.menu_item_id, servings: x.servings })),
        lunch: plan.lunch.map(x => ({ menu_item_id: x.menu_item_id, servings: x.servings })),
        snack: plan.snack.map(x => ({ menu_item_id: x.menu_item_id, servings: x.servings })),
        notes: plan.notes
      });
      setSuccess("Menu plan saved!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(formatError(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end flex-wrap">
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen</label>
          <select value={kitchenId} onChange={e => setKitchenId(e.target.value)}
            className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
            {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
        </div>
        <button onClick={copyFromPrevious}
          className="flex items-center gap-1.5 text-sm border-2 border-[#E8956D] text-[#E8956D] rounded-lg px-4 py-2 font-bold hover:bg-[#E8956D]/10 transition-colors">
          <Copy size={14} /> Copy Yesterday's Plan
        </button>
        {totalServings > 0 && (
          <span className="text-sm font-bold text-[#5C5147] bg-[#F4F3F0] px-3 py-2 rounded-lg ml-auto">
            {totalServings.toLocaleString()} total servings
          </span>
        )}
      </div>

      {loadingPlan && <div className="flex justify-center py-4"><Loader size={20} className="animate-spin text-[#E8956D]" /></div>}

      {!loadingPlan && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {MEAL_TYPES.map(mealType => {
            const used = plan[mealType].map(x => x.menu_item_id);
            return (
              <div key={mealType} className={`border-2 rounded-xl overflow-hidden ${MEAL_COLORS[mealType]}`}>
                <div className={`px-4 py-3 border-b-2 ${MEAL_COLORS[mealType]}`}>
                  <h3 className="font-bold capitalize">{mealType}</h3>
                  <p className="text-xs opacity-70">{plan[mealType].reduce((s, i) => s + (i.servings || 0), 0).toLocaleString()} servings</p>
                </div>
                <div className="bg-white p-3 space-y-2 min-h-24">
                  {plan[mealType].map((item, idx) => (
                    <div key={`k-${idx}`} className="flex items-center gap-2 bg-[#FAFAF8] border border-[#E5E1DB] rounded-lg px-3 py-2">
                      <span className="text-sm flex-1 truncate font-medium text-[#3A322C]">{item.menu_item_name || item.menu_item_id}</span>
                      <input type="number" min="0" placeholder="Servings"
                        value={item.servings || ""}
                        onChange={e => updateServings(mealType, idx, e.target.value)}
                        className="border border-[#E5E1DB] rounded px-2 py-1 text-xs w-20 outline-none focus:border-[#E8956D] text-center" />
                      <button onClick={() => removeItem(mealType, idx)} className="text-[#C4BAAF] hover:text-[#C2624E]"><X size={13} /></button>
                    </div>
                  ))}
                  <select className="w-full border border-dashed border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D]"
                    value="" onChange={e => { addMealItem(mealType, e.target.value); e.target.value = ""; }}>
                    <option value="">+ Add dish...</option>
                    {menuItems.filter(i => !used.includes(i.id)).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {menuItems.length === 0 && (
        <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-4 py-3 text-sm text-[#D98032]">
          No menu items found. <a href="/menu?tab=items" className="font-bold underline">Add menu items first</a>, then come back to plan your daily meals.
        </div>
      )}

      <div>
        <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label>
        <input value={plan.notes} onChange={e => setPlan(p => ({ ...p, notes: e.target.value }))}
          placeholder="Special instructions for today..." className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" />
      </div>

      {error && <div className="bg-[#FBEAE7] rounded-lg px-4 py-2.5 text-sm text-[#C2624E]">{error}</div>}
      {success && <div className="bg-[#EDF2EE] rounded-lg px-4 py-2.5 text-sm text-[#637C64] flex items-center gap-2"><Check size={15} />{success}</div>}

      <button onClick={savePlan} disabled={saving}
        className="w-full bg-[#637C64] text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2 hover:bg-[#4e6350] disabled:opacity-60 transition-colors">
        {saving ? <Loader size={16} className="animate-spin" /> : <CalendarDays size={16} />}
        {saving ? "Saving..." : "Save Today's Menu Plan"}
      </button>
    </div>
  );
}

// ─── TARGET PRICES ────────────────────────────────────────────────────────────
function TargetPricesSection() {
  const { confirm, ConfirmModal } = useConfirm();
  const [prices, setPrices] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [newItem, setNewItem] = useState("");
  const [newRate, setNewRate] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => { fetchPrices(); }, []);

  const fetchPrices = async () => {
    try { const r = await adminAPI.getTargetPrices(); setPrices(r.data || []); }
    catch (e) { if (process.env.NODE_ENV === 'development') console.error('prices fetch:', e); }
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      await adminAPI.updateTargetPrice(id, { menu_item: prices.find(p => p.id === id)?.menu_item, target_rate: parseFloat(editVal) || 0 });
      setEditingId(null); fetchPrices();
      setSuccess("Saved!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); } setSaving(false);
  };

  const addPrice = async () => {
    if (!newItem || !newRate) return;
    setSaving(true);
    try {
      await adminAPI.createTargetPrice({ menu_item: newItem, target_rate: parseFloat(newRate) || 0 });
      setNewItem(""); setNewRate(""); fetchPrices();
      setSuccess("Added!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); } setSaving(false);
  };

  const deletePrice = async (id) => {
    if (!await confirm("Delete target price?")) return;
    await adminAPI.deleteTargetPrice(id);
    fetchPrices();
  };

  return (
    <div className="space-y-3">
      {success && <div className="bg-[#EDF2EE] rounded-lg px-4 py-2.5 text-sm text-[#637C64] flex items-center gap-2"><Check size={15} />{success}</div>}

      <p className="text-xs text-[#8A7E73]">Set the target cost per serving for each menu item. The AI uses this to flag when actual costs exceed your targets.</p>

      <div className="border border-[#E5E1DB] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F3F0]">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">Menu Item</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">Target Rate (₹/serving)</th>
              <th className="w-20" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F4F3F0]">
            {prices.map(p => (
              <tr key={p.id} className="hover:bg-[#FAFAF8]">
                <td className="px-4 py-3 font-medium text-[#3A322C]">{p.menu_item}</td>
                <td className="px-4 py-3">
                  {editingId === p.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[#8A7E73]">₹</span>
                      <input type="number" step="0.5" autoFocus value={editVal} onChange={e => setEditVal(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && saveEdit(p.id)}
                        className="border-2 border-[#E8956D] rounded-lg px-2 py-1 text-sm w-24 outline-none" />
                      <button onClick={() => saveEdit(p.id)} className="bg-[#E8956D] text-white rounded px-2 py-1 text-xs font-bold"><Check size={12} /></button>
                      <button onClick={() => setEditingId(null)} className="text-[#8A7E73]"><X size={14} /></button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingId(p.id); setEditVal(String(p.target_rate)); }}
                      className="font-bold text-[#E8956D] hover:underline text-base">₹{p.target_rate}</button>
                  )}
                </td>
                <td className="px-3 py-3">
                  <button onClick={() => deletePrice(p.id)} className="text-[#C4BAAF] hover:text-[#C2624E]"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {/* Add new row */}
            <tr className="bg-[#FAFAF8]">
              <td className="px-4 py-3">
                <input value={newItem} onChange={e => setNewItem(e.target.value)}
                  placeholder="Menu item name (e.g. Sambar Rice)" onKeyDown={e => e.key === "Enter" && addPrice()}
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-[#8A7E73]">₹</span>
                  <input type="number" step="0.5" value={newRate} onChange={e => setNewRate(e.target.value)}
                    placeholder="0.00" onKeyDown={e => e.key === "Enter" && addPrice()}
                    className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm w-28 outline-none focus:border-[#E8956D]" />
                </div>
              </td>
              <td className="px-3 py-3">
                <button onClick={addPrice} disabled={!newItem || !newRate || saving}
                  className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-1.5 text-xs font-bold hover:bg-[#D48661] disabled:opacity-50 flex items-center gap-1">
                  <Plus size={12} /> Add
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {ConfirmModal}
    </div>
  );
}

// ─── ACCESS RESTRICTED PLACEHOLDER ──────────────────────────────────────────
function AccessRestricted({ message, redirectTo, linkText }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
      <div className="w-12 h-12 bg-[#F4F3F0] rounded-xl flex items-center justify-center">
        <Lock size={22} className="text-[#C4BAAF]" />
      </div>
      <p className="text-sm text-[#8A7E73]">{message}</p>
      {redirectTo && (
        <button onClick={() => navigate(redirectTo)}
          className="text-xs text-[#E8956D] font-bold underline hover:no-underline">
          {linkText || "Go to Admin Settings"}
        </button>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function DailyEntry() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { kitchens: ctxKitchens, selectedKitchen } = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  const [masterItems, setMasterItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.is_admin;
  const canCreateStock = isAdmin || user?.permissions?.stores_create;
  const canPlanMenu = isAdmin || user?.permissions?.menu_create || user?.permissions?.menu_view;

  useEffect(() => {
    // Fetch kitchens if context is empty
    if (ctxKitchens && ctxKitchens.length > 0) {
      setKitchens(ctxKitchens);
    } else {
      usersAPI.getKitchens().then(r => {
        const ks = r.data || [];
        if (ks.length > 0) setKitchens(ks);
      }).catch(() => {});
    }
  }, [ctxKitchens]);

  useEffect(() => {
    adminAPI.getMasterItems().then(m => {
      setMasterItems(m.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex justify-center py-24"><Loader size={28} className="animate-spin text-[#E8956D]" /></div>
  );

  return (
    <div className="space-y-6 max-w-5xl" data-testid="daily-entry">
      <div>
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Daily Entry</h1>
        <p className="text-sm text-[#8A7E73] mt-0.5">Enter today's stock inward and menu plan</p>
      </div>

      {/* AI tip banner */}
      <div className="bg-gradient-to-r from-[#E8956D]/10 to-[#5C5147]/5 border border-[#E8956D]/20 rounded-xl px-5 py-4 flex items-center gap-4">
        <Sparkles size={20} className="text-[#E8956D] flex-shrink-0" />
        <div>
          <p className="text-sm font-bold text-[#3A322C]">The more you enter here, the smarter the AI gets</p>
          <p className="text-xs text-[#8A7E73] mt-0.5">Stock records power the Stock Forecast · Menu plans enable Breakfast vs Lunch analysis</p>
        </div>
        <button onClick={() => navigate("/ai")} className="ml-auto text-xs bg-[#E8956D] text-[#3A322C] px-3 py-1.5 rounded-lg font-bold hover:bg-[#D48661] flex-shrink-0 flex items-center gap-1">
          Open AI <ChevronRight size={12} />
        </button>
      </div>

      {/* Stock Inward — permission: stores_create */}
      <Section title="Stock Inward — Multi-Item Entry" icon={ArrowDown}
        badge={<span className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-2 py-0.5 rounded-full font-bold ml-2">Enter all today's purchases at once</span>}>
        {canCreateStock
          ? <StockInwardSection kitchens={kitchens} masterItems={masterItems} />
          : <AccessRestricted
              message="You need 'Stores — Create' permission to record stock inward. Ask your admin to enable it in User Management → Roles."
              redirectTo={isAdmin ? "/users?tab=Roles" : null}
            />
        }
      </Section>

      {/* Menu Plan — permission: menu_view or admin */}
      <Section title="Today's Menu Plan" icon={CalendarDays}
        badge={<span className="text-xs bg-[#637C64]/10 text-[#637C64] px-2 py-0.5 rounded-full font-bold ml-2">Breakfast + Lunch + Snack</span>}>
        {canPlanMenu
          ? <MenuPlanSection kitchens={kitchens} />
          : <AccessRestricted
              message="You need 'Menu/Recipes — View' permission to manage daily plans."
              redirectTo={isAdmin ? "/users?tab=Roles" : null}
            />
        }
      </Section>

      {/* Target Prices — ADMIN ONLY */}
      <Section title="Cost Analysis — Target Prices" icon={TrendingUp}
        badge={
          isAdmin
            ? <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-bold ml-2">Admin only · Click ₹ to edit</span>
            : <span className="text-xs bg-[#F4F3F0] text-[#8A7E73] px-2 py-0.5 rounded-full font-bold ml-2 flex items-center gap-1"><Lock size={10} /> Admin only</span>
        }>
        {isAdmin
          ? <TargetPricesSection />
          : <AccessRestricted
              message="Target prices can only be set by administrators. This ensures consistent cost benchmarks across all kitchens."
              redirectTo={null}
            />
        }
      </Section>
    </div>
  );
}
